import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  ClipboardList,
  TrendingUp,
  Users,
  Scale,
  Inbox,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { TabType } from "../../pages/teacher/Dashboard";
import { useT } from "../../../stores/uiLang";
import { REVIEW_T } from "../../../i18n/teacherDashboard";

const SIDEBAR_COLLAPSED_KEY = "instructor-sidebar-collapsed";

interface NavItem {
  id: TabType;
  label: string;
  icon: React.ElementType;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// Run E2 J2: PFL items hidden for Project Explore: "Resources > Guidebooks"
// (PFL state standards), "Scaffolding Settings" and "Interview Settings".
// Labels follow the shared UI language (EN / ES).
// Run E2 J1: student-teacher chat removed (K-5). "Communication > Chat" group gone.
const NAV_T = {
  en: {
    teaching: "Teaching",
    overview: "Overview",
    gradebook: "Gradebook",
    assignments: "Assignments",
    students: "Student Progress",
    configuration: "Configuration",
    classManagement: "Class Management",
    gradeScales: "Grade Scales",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    closeMenu: "Close navigation menu",
    navigation: "Navigation",
  },
  es: {
    teaching: "Enseñanza",
    overview: "Resumen",
    gradebook: "Calificaciones",
    assignments: "Tareas",
    students: "Progreso de los estudiantes",
    configuration: "Configuración",
    classManagement: "Administrar clases",
    gradeScales: "Escalas de calificación",
    expand: "Abrir barra lateral",
    collapse: "Cerrar barra lateral",
    closeMenu: "Cerrar el menú de navegación",
    navigation: "Navegación",
  },
};

function buildNavGroups(t: (typeof NAV_T)["en"], reviewLabel: string): NavGroup[] {
  return [
    {
      label: t.teaching,
      items: [
        { id: "overview", label: t.overview, icon: LayoutDashboard },
        { id: "gradebook", label: t.gradebook, icon: BookOpen },
        // Run E2 J6 (D10): score and release spoken and written answers.
        { id: "review", label: reviewLabel, icon: Inbox },
        { id: "assignments", label: t.assignments, icon: ClipboardList },
        { id: "students", label: t.students, icon: TrendingUp },
      ],
    },
    {
      label: t.configuration,
      items: [
        { id: "class-management", label: t.classManagement, icon: Users },
        { id: "grade-scales", label: t.gradeScales, icon: Scale },
      ],
    },
  ];
}

interface InstructorSidebarProps {
  readonly activeTab: TabType;
  readonly onTabChange: (tab: TabType) => void;
  readonly isMobileOpen: boolean;
  readonly onMobileClose: () => void;
  readonly onMobileOpen: () => void;
}

export function InstructorSidebar({
  activeTab,
  onTabChange,
  isMobileOpen,
  onMobileClose,
  onMobileOpen,
}: InstructorSidebarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useT(NAV_T);
  const reviewT = useT(REVIEW_T);
  const navGroups = buildNavGroups(t, reviewT.navLabel);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  // Read active tab from URL query param
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    const allTabs = navGroups.flatMap((g) => g.items.map((i) => i.id));
    if (
      tabFromUrl &&
      allTabs.includes(tabFromUrl as TabType) &&
      tabFromUrl !== activeTab
    ) {
      onTabChange(tabFromUrl as TabType);
    }
  }, [searchParams]);

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Auto-collapse on tablet
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 768 && width < 1024) {
        setIsCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleTabChange = (tabId: TabType) => {
    onTabChange(tabId);
    setSearchParams({ tab: tabId });
    onMobileClose();
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    const isActive = activeTab === item.id;

    return (
      <button
        key={item.id}
        onClick={() => handleTabChange(item.id)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? "bg-indigo-50 text-indigo-700 rounded-r-lg border-l-4 !border-indigo-600"
            : "text-gray-700 hover:bg-gray-50 hover:text-indigo-700 rounded-lg"
        }`}
        title={isCollapsed ? item.label : undefined}
      >
        <Icon
          className={`h-5 w-5 flex-shrink-0 ${
            isActive ? "text-indigo-600" : "text-gray-500"
          }`}
        />
        {!isCollapsed && (
          <span className="whitespace-nowrap">{item.label}</span>
        )}
      </button>
    );
  };

  const renderNavGroup = (group: NavGroup, index: number) => (
    <div key={group.label} className={index > 0 ? "mt-6" : ""}>
      {!isCollapsed && (
        <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
          {group.label}
        </h3>
      )}
      {isCollapsed && index > 0 && (
        <div className="border-t border-gray-200 my-3 mx-2" />
      )}
      <nav className="space-y-1">{group.items.map(renderNavItem)}</nav>
    </div>
  );

  // Desktop sidebar
  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto py-4 px-2 overflow-hidden">
        {navGroups.map((group, index) => renderNavGroup(group, index))}
      </div>

      {/* Edge-positioned collapse button */}
      <button
        onClick={toggleCollapse}
        className="
          absolute top-1/2 -translate-y-1/2 -right-3
          w-6 h-6 rounded-full
          bg-indigo-600 border border-indigo-600 shadow-sm
          hover:shadow-md hover:bg-indigo-700
          text-white
          transition-all duration-150
          flex items-center justify-center
          z-10
        "
        title={isCollapsed ? t.expand : t.collapse}
      >
        {isCollapsed ? (
          <ChevronRight className="h-3.5 w-3.5" />
        ) : (
          <ChevronLeft className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar - hidden on mobile */}
      <aside
        className={`hidden md:flex flex-col bg-white border-r border-gray-200 transition-all duration-200 flex-shrink-0 h-[calc(100vh-72px)] sticky top-[72px] ${
          isCollapsed ? "w-16" : "w-60"
        }`}
      >
        {sidebarContent}
      </aside>
      {/* Mobile drawer overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <button
            type="button"
            className="fixed inset-0 w-full h-full bg-gray-600 bg-opacity-50 transition-opacity cursor-default"
            onClick={onMobileClose}
            aria-label={t.closeMenu}
          />

          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl flex flex-col">
            {/* Drawer header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t.navigation}
              </h2>
              <button
                onClick={onMobileClose}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer content */}
            <div className="flex-1 overflow-y-auto py-4 px-2">
              {navGroups.map((group, index) => (
                <div key={group.label} className={index > 0 ? "mt-6" : ""}>
                  <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {group.label}
                  </h3>
                  <nav className="space-y-1">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeTab === item.id;

                      return (
                        <button
                          key={item.id}
                          onClick={() => handleTabChange(item.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                            isActive
                              ? "bg-indigo-50 text-indigo-700 rounded-r-lg border-l-4 border-indigo-600"
                              : "text-gray-700 hover:bg-gray-50 hover:text-indigo-700 rounded-lg"
                          }`}
                        >
                          <Icon
                            className={`h-5 w-5 flex-shrink-0 ${
                              isActive ? "text-indigo-600" : "text-gray-500"
                            }`}
                          />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}{" "}
    </>
  );
}
